import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { SecretResponse } from './type';
import { clientFetch } from '~/lib/.client/fetch';
import { invariant } from '~/utils/invariant';
import { projectStore } from '~/lib/stores/project';

const getSecrets = async (projectId?: string) => {
  invariant(projectId);
  const response = await clientFetch(`/api/supabase/projects/${projectId}/secrets`);

  const result = await response.json<SecretResponse[]>();

  return result;
};

export const useGetSecrets = () => {
  const projectId = projectStore.projectId.get();
  return useQuery({
    queryKey: ['secrets', projectId],
    queryFn: () => getSecrets(projectId),
    enabled: !!projectId,
    retry: false,
  });
};

const createSecrets = async ({
  secrets,
}: {
  secrets: {
    name: string;
    value: string;
  }[];
}) => {
  const projectId = projectStore.projectId.get();
  invariant(projectId);
  const response = await clientFetch(`/api/supabase/projects/${projectId}/secrets`, {
    method: 'POST',
    body: JSON.stringify({ secrets }),
  });

  const result = await response.json<{ success: boolean; error?: string }>();

  if (!result.success) {
    throw new Error(typeof result.error === 'string' ? result.error : 'There was a problem with your request.');
  }

  return result;
};

export const useCreateSecrets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSecrets,
    onSuccess: () => {
      toast.success(`Secrets created successfully.`);
      queryClient.refetchQueries({
        queryKey: ['secrets'],
      });
    },
    onError: (error: { message?: string }) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'There was a problem with your request.';
      toast.error(message);
    },
  });
};

const deleteSecrets = async ({ secretNames }: { secretNames: string[] }) => {
  const projectId = projectStore.projectId.get();
  invariant(projectId);
  const response = await clientFetch(`/api/supabase/projects/${projectId}/secrets`, {
    method: 'DELETE',
    body: JSON.stringify({ secretNames }),
  });

  const result = await response.json<{ success: boolean; error?: string }>();

  if (!result.success) {
    throw new Error(typeof result.error === 'string' ? result.error : 'There was a problem with your request.');
  }

  return result;
};

export const useDeleteSecrets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSecrets,
    onSuccess: () => {
      toast.success(`Secrets deleted successfully.`);
      queryClient.invalidateQueries({
        queryKey: ['secrets'],
      });
    },
    onError: (error: { message?: string }) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'There was a problem with your request.';
      toast.error(message);
    },
  });
};
